import { Dispatch, useEffect } from 'react'

import { deriveFiltersFromDateFilter } from 'modules/ai_reports/components/DatasourceTypeRenderer'
import { datatableFiltersToJsonLogicFilters } from 'modules/ai_reports/components/DatasourceTypeRenderer/filters'
import { ReportSchemaType } from 'modules/ai_reports/lib/types'
import { AiReportsResult } from 'modules/ai_reports/lib/useCreateAnalyticsReportActionResolver'

import { ReportAction, ReportState } from './useReportCoreState'

type UseReportFiltersSyncOptions = {
  state: ReportState
  schema: ReportSchemaType
  dispatch: Dispatch<ReportAction>
  agentState: AiReportsResult | null
  setAgentState: (state: AiReportsResult) => void
  datasourceId: string
}

/**
 * Handles bidirectional synchronization between report filters and agent state.
 * - Syncs agent state changes to local state
 * - Syncs date filter changes to agent
 * - Syncs datatable filter changes to agent
 */
export function useReportFiltersSync({
  state,
  schema,
  dispatch,
  agentState,
  setAgentState,
  datasourceId,
}: UseReportFiltersSyncOptions) {
  // Sync agent state changes to local state
  useEffect(() => {
    if (agentState) {
      dispatch({ type: 'AGENT_STATE_CHANGED', payload: agentState })
    }
  }, [
    agentState?.sqlAnswer?.sql,
    agentState?.sqlAnswer?.description,
    agentState?.filters?.jsonLogicFilters,
    agentState?.filters?.dateRange?.from,
    agentState?.filters?.dateRange?.to,
    agentState?.filters?.snapshotDate,
  ])

  // Sync date filter changes to agent state
  useEffect(() => {
    if (!agentState) {
      return
    }

    const derivedDateFilters = deriveFiltersFromDateFilter(state.dateFilter, datasourceId)
    setAgentState({
      ...agentState,
      filters: {
        ...agentState.filters,
        ...derivedDateFilters,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.dateFilter, datasourceId])

  // Sync datatable filters changes to agent state
  useEffect(() => {
    if (!agentState) {
      return
    }

    const jsonLogicFilters = datatableFiltersToJsonLogicFilters(
      state.datatableFilters || {},
      schema
    )
    setAgentState({
      ...agentState,
      filters: {
        ...agentState.filters,
        jsonLogicFilters: JSON.stringify(jsonLogicFilters),
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.datatableFilters])
}
